import { Validators } from '@angular/forms';
import { TypedFormControl, TypedFormGroup } from '@principle-theorem/ng-shared';
import {
  DepositBookingContext,
  DepositCaptureStrategy,
  IDepositTreatmentOverride,
  type IDepositAmount,
} from '@principle-theorem/principle-core/interfaces';
import { type DayOfWeek } from '@principle-theorem/shared';
import { DepositAmountConversion } from './deposit-amount-conversion';

interface IDepositTreatmentTemplateFormValue {
  depositRequired: boolean;
  /** Dollars. Converted to persisted cents by `toOverride`. */
  amountValue?: number;
  captureStrategy: DepositCaptureStrategy;
  /** Empty means inherit the blanket rule rather than override it. */
  appliesTo: DepositBookingContext[];
  depositRequiredDays: DayOfWeek[];
}

interface IDepositTreatmentTemplateFormOptions {
  readonly depositRequired: boolean;
  readonly amount?: IDepositAmount;
  readonly captureStrategy: DepositCaptureStrategy;
  readonly inheritsCaptureStrategy?: boolean;
  readonly appliesTo: DepositBookingContext[];
  readonly depositRequiredDays: DayOfWeek[];
}

/**
 * The deposit configuration for a single treatment template.
 *
 * A template deposit is always a fixed dollar amount. Percentages of treatment
 * cost were configurable here but never worked end to end, because a template
 * is chosen before a treatment is priced. They are set on treatment
 * configurations instead, where a price exists.
 */
export class DepositTreatmentTemplateFormGroup extends TypedFormGroup<IDepositTreatmentTemplateFormValue> {
  private _inheritsCaptureStrategy: boolean;

  constructor(options: IDepositTreatmentTemplateFormOptions) {
    super({
      depositRequired: new TypedFormControl(options.depositRequired),
      amountValue: new TypedFormControl<number | undefined>(
        options.amount
          ? DepositAmountConversion.toFormAmount(options.amount)
          : undefined
      ),
      captureStrategy: new TypedFormControl(
        options.captureStrategy,
        Validators.required
      ),
      appliesTo: new TypedFormControl(options.appliesTo),
      depositRequiredDays: new TypedFormControl(
        options.depositRequiredDays,
        Validators.required
      ),
    });
    this._inheritsCaptureStrategy = options.inheritsCaptureStrategy ?? false;
    this.controls.captureStrategy.valueChanges.subscribe(() => {
      this._inheritsCaptureStrategy = false;
    });
    this.updateAmountValidators();
  }

  /**
   * True while this template still takes its capture strategy from the brand
   * default. The editing surfaces use it to show the inherited value rather
   * than presenting a select that always looks deliberately set.
   */
  get inheritsCaptureStrategy(): boolean {
    return this._inheritsCaptureStrategy;
  }

  restoreInheritedCaptureStrategy(): void {
    this._inheritsCaptureStrategy = true;
  }

  /**
   * Amounts are dollars above zero, and do not apply while the template
   * requires no deposit.
   */
  updateAmountValidators(): void {
    if (!this.controls.depositRequired.value) {
      this.controls.amountValue.clearValidators();
      this.controls.amountValue.updateValueAndValidity({ emitEvent: false });
      this.controls.depositRequiredDays.clearValidators();
      this.controls.depositRequiredDays.updateValueAndValidity({
        emitEvent: false,
      });
      return;
    }

    this.controls.depositRequiredDays.setValidators(Validators.required);
    this.controls.depositRequiredDays.updateValueAndValidity({
      emitEvent: false,
    });
    this.controls.amountValue.setValidators([
      Validators.required,
      Validators.min(0.01),
    ]);
    this.controls.amountValue.updateValueAndValidity({ emitEvent: false });
  }

  toOverride(): IDepositTreatmentOverride {
    const value = this.getRawValue();
    if (!value.depositRequired) {
      return { depositRequired: false };
    }
    return {
      depositRequired: true,
      amount: DepositAmountConversion.fromFormAmount(value.amountValue ?? 0),
      ...(!this._inheritsCaptureStrategy
        ? { captureStrategy: value.captureStrategy }
        : {}),
      depositRequiredDays: value.depositRequiredDays,
      ...(value.appliesTo.length ? { appliesTo: value.appliesTo } : {}),
    };
  }
}
